import { scaleLinear, scalePoint } from 'd3-scale';
import { line as d3Line } from 'd3-shape';
import { formatIsoDateDDMMYYYY } from '@/utils/date';

export type ScorecardChartSeries = {
  id: string;
  label: string;
  fieldId: string;
  color?: string;
};

export type ScorecardChartEntry = {
  index: number;
  date?: string;
  values: Record<string, number | null | undefined>;
};

export type ScorecardChartSvgOptions = {
  entries: ScorecardChartEntry[];
  series: ScorecardChartSeries[];
  width?: number;
  height?: number;
  minValue?: number;
  maxValue?: number;
};

export type ScorecardChartSvgResult = {
  svg: string;
  width: number;
  height: number;
};

const DEFAULT_WIDTH = 560;
const DEFAULT_HEIGHT = 160;
const DEFAULT_MIN = 0;
const DEFAULT_MAX = 10;

const AXIS_COLOR = '#9CA3AF';
const GRID_COLOR = '#E5E7EB';
const LABEL_COLOR = '#6B7280';

function escapeSvgText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function coerceDateLabel(value: string | undefined): string {
  if (!value) return 'N/D';
  return formatIsoDateDDMMYYYY(value);
}

export function buildScorecardChartSvg({
  entries,
  series,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  minValue = DEFAULT_MIN,
  maxValue = DEFAULT_MAX,
}: ScorecardChartSvgOptions): ScorecardChartSvgResult {
  const safeEntries = entries.length > 0 ? entries : [{ index: 0, values: {} }];
  const margin = { top: 10, right: 16, bottom: 26, left: 28 };

  const chartHeight = height;
  const innerWidth = Math.max(1, width - margin.left - margin.right);
  const innerHeight = Math.max(1, chartHeight - margin.top - margin.bottom);

  const legendColumns = series.length <= 2 ? series.length : 2;
  const legendRows = Math.ceil(series.length / Math.max(legendColumns, 1));
  const legendPadding = series.length > 0 ? 10 : 0;
  const legendRowHeight = 12;
  const legendHeight = series.length > 0 ? legendPadding + legendRows * legendRowHeight : 0;

  const totalHeight = chartHeight + legendHeight;

  const xDomain = safeEntries.map((entry) => entry.index);
  const xScale = scalePoint<number>().domain(xDomain).range([0, innerWidth]).padding(0.5);

  const yScale = scaleLinear().domain([minValue, maxValue]).range([innerHeight, 0]);

  const svgParts: string[] = [];
  svgParts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${totalHeight}" width="${width}" height="${totalHeight}" style="width:100%;height:auto;display:block;">`
  );

  svgParts.push(`<g transform="translate(${margin.left}, ${margin.top})">`);

  // Grid lines + y-axis labels
  for (let value = minValue; value <= maxValue; value += 1) {
    const y = yScale(value);
    svgParts.push(
      `<line x1="0" y1="${y}" x2="${innerWidth}" y2="${y}" stroke="${GRID_COLOR}" stroke-width="1" />`
    );
    svgParts.push(
      `<text x="-6" y="${y + 3}" text-anchor="end" font-size="8" fill="${LABEL_COLOR}">${value}</text>`
    );
  }

  // Axis baseline
  svgParts.push(
    `<line x1="0" y1="${innerHeight}" x2="${innerWidth}" y2="${innerHeight}" stroke="${AXIS_COLOR}" stroke-width="1" />`
  );

  // X labels
  safeEntries.forEach((entry) => {
    const x = xScale(entry.index) ?? 0;
    const label = escapeSvgText(coerceDateLabel(entry.date));
    svgParts.push(
      `<text x="${x}" y="${innerHeight + 14}" text-anchor="middle" font-size="8" fill="${LABEL_COLOR}">${label}</text>`
    );
  });

  const lineGenerator = d3Line<{ index: number; value: number | null }>()
    .x((d: { index: number; value: number | null }) => xScale(d.index) ?? 0)
    .y((d: { index: number; value: number | null }) => yScale(d.value ?? minValue))
    .defined(
      (d: { index: number; value: number | null }) => d.value !== null && d.value !== undefined
    );

  series.forEach((seriesItem) => {
    const color = seriesItem.color ?? '#111827';
    const seriesData = safeEntries.map((entry) => ({
      index: entry.index,
      value: (() => {
        const raw = entry.values[seriesItem.fieldId];
        if (raw === null || raw === undefined) return null;
        if (typeof raw === 'number') {
          if (!Number.isFinite(raw)) return null;
          return Math.min(maxValue, Math.max(minValue, raw));
        }
        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) return null;
        return Math.min(maxValue, Math.max(minValue, parsed));
      })(),
    }));

    const path = lineGenerator(seriesData);
    if (path) {
      svgParts.push(`<path d="${path}" fill="none" stroke="${color}" stroke-width="1.5" />`);
    }

    seriesData.forEach((point) => {
      if (point.value === null || point.value === undefined) return;
      const cx = xScale(point.index) ?? 0;
      const cy = yScale(point.value);
      svgParts.push(`<circle cx="${cx}" cy="${cy}" r="2.5" fill="${color}" />`);
    });
  });

  svgParts.push(`</g>`);

  if (series.length > 0) {
    const legendColumnWidth = (width - margin.left - margin.right) / Math.max(legendColumns, 1);
    const legendStartY = chartHeight + legendPadding;
    series.forEach((seriesItem, index) => {
      const col = index % legendColumns;
      const row = Math.floor(index / legendColumns);
      const x = margin.left + col * legendColumnWidth;
      const y = legendStartY + row * legendRowHeight;
      const color = seriesItem.color ?? '#111827';
      const label = escapeSvgText(seriesItem.label);

      svgParts.push(
        `<line x1="${x}" y1="${y}" x2="${x + 18}" y2="${y}" stroke="${color}" stroke-width="2" />`
      );
      svgParts.push(`<circle cx="${x + 9}" cy="${y}" r="2.5" fill="${color}" />`);
      svgParts.push(
        `<text x="${x + 24}" y="${y + 3}" font-size="8" fill="${LABEL_COLOR}">${label}</text>`
      );
    });
  }

  svgParts.push(`</svg>`);

  return {
    svg: svgParts.join(''),
    width,
    height: totalHeight,
  };
}
